// frontend/src/config/api.js
// API configuration and backend switching functionality

let getViteEnv;
try {
  // Only import in browser/Vite context
  getViteEnv = require("./env.js").getViteEnv;
} catch (e) {
  getViteEnv = () => undefined;
}

const isJestEnvironment = () => {
  // Check if we're in a test environment (Vitest or Jest)
  // Vitest: import.meta.env.MODE === "test"
  // Jest: process.env.NODE_ENV === "test"
  return (
    (typeof import.meta !== "undefined" && import.meta.env?.MODE === "test") ||
    (typeof process !== "undefined" && process.env?.NODE_ENV === "test")
  );
};

const getEnvVar = (key, defaultValue = undefined) => {
  if (isJestEnvironment()) {
    // In test environment, try import.meta.env first (Vitest), then process.env (Jest fallback)
    if (typeof import.meta !== "undefined" && import.meta.env) {
      // For Vitest, only VITE_ prefixed vars are available via import.meta.env
      // For non-VITE_ vars in tests, we'd need to check process.env
      const viteKey = key.startsWith("VITE_") ? key : `VITE_${key}`;
      return import.meta.env[viteKey] || import.meta.env[key] || 
             (typeof process !== "undefined" ? process.env[key] : undefined) || defaultValue;
    }
    // Fallback for Jest
    return typeof process !== "undefined" ? (process.env[key] || defaultValue) : defaultValue;
  }
  if (typeof window !== "undefined" && getViteEnv) {
    return getViteEnv(key, defaultValue);
  }
  return defaultValue;
};

// Get backend location from localStorage or default
const getBackendLocation = () => {
  const stored = localStorage.getItem("alertique_backend_location");

  // Check environment variable if no localStorage value
  if (!stored) {
    const envBackend = getEnvVar("VITE_BACKEND_LOCATION");
    if (envBackend) {
      return envBackend;
    }
  }

  return stored || "local";
};

// Get database type from localStorage or default
const getDatabaseType = () => {
  const stored = localStorage.getItem("alertique_database_type");

  // Check environment variable if no localStorage value
  if (!stored) {
    const envDatabase = getEnvVar("VITE_DATABASE_TYPE");
    if (envDatabase) {
      return envDatabase;
    }
  }

  return stored || "sqlite";
};

// Get environment from localStorage or default
const getEnvironment = () => {
  const stored = localStorage.getItem("alertique_environment");

  // Check environment variable if no localStorage value
  if (!stored) {
    const envEnvironment = getEnvVar("VITE_ENVIRONMENT");
    if (envEnvironment) {
      return envEnvironment;
    }
  }

  return stored || "development";
};

// Check if we're running in local development
const isLocalDevelopment = () => {
  if (typeof window === "undefined") {
    return false; // Server-side rendering
  }

  const hostname = window.location.hostname;
  const port = window.location.port;

  const isLocalhost = hostname === "localhost" || hostname === "127.0.0.1";
  const isDevPort =
    port === "5173" || port === "5174" || port === "3000" || port === "8080";

  // Use safe environment variable access
  const isDevMode = getEnvVar("MODE") === "development";

  return isLocalhost || isDevPort || isDevMode;
};

// Detect if this is a production deployment
const isProductionDeployment = () => {
  if (typeof window === "undefined") {
    return false; // Server-side rendering
  }

  const hostname = window.location.hostname;

  // Check for production domains (Vercel, custom domain, Render)
  const isVercel =
    hostname.includes("vercel.app") ||
    hostname.includes("alertique.com") ||
    hostname.includes("alertique") ||
    hostname.includes("onrender.com");

  // Use safe environment variable access
  const isProdEnv =
    getEnvVar("MODE") === "production" ||
    getEnvVar("VITE_ENV") === "production";

  // Only log in development if explicitly enabled
  if (getEnvVar("DEV") && getEnvVar("VITE_DEBUG_API") === "true") {
    console.log("🔧 Production deployment check:", {
      hostname,
      isVercel,
      mode: getEnvVar("MODE", "unknown"),
      viteEnv: getEnvVar("VITE_ENV", "unknown"),
      isProdEnv,
      result: isVercel || isProdEnv,
    });
  }

  return isVercel || isProdEnv;
};

// Check if preview mode is enabled
const isPreviewMode = () => {
  return localStorage.getItem("alertique_preview_mode") === "true";
};

// Backend Configuration
const BACKEND_CONFIG = {
  local: {
    baseURL: "http://127.0.0.1:5001",
    name: "Local Backend",
    description: "Backend running on your local machine",
    port: 5001,
  },
  remote: {
    baseURL: "https://alertique-backend.onrender.com",
    name: "Remote Backend",
    description: "Backend deployed on Render.com",
    port: 10000,
  },
};

// Database Configuration
const DATABASE_CONFIG = {
  sqlite: {
    name: "SQLite",
    description: "Local SQLite database",
    type: "SQLite",
  },
  postgresql: {
    name: "PostgreSQL",
    description: "Production PostgreSQL database",
    type: "PostgreSQL",
  },
};

// Legacy API Configuration (for backward compatibility)
const API_CONFIG = {
  development: {
    baseURL: "http://127.0.0.1:5001",
    name: "Development (SQLite)",
    description: "Local development with SQLite database",
    port: 5001,
    database: "SQLite",
  },
  production: {
    baseURL: "https://alertique-backend.onrender.com",
    name: "Production (PostgreSQL)",
    description: "Deployed production with PostgreSQL database",
    port: 10000,
    database: "PostgreSQL",
  },
};

// Get current backend location
export const currentBackendLocation = getBackendLocation();

// Get current database type
export const currentDatabaseType = getDatabaseType();

// Get current environment (legacy support)
export const currentEnvironment = getEnvironment();

// Check if we're running locally
export const isLocal = isLocalDevelopment();

// Check if preview mode is enabled
export const isPreview = isPreviewMode();

// Check if we should show development features
export const showDevFeatures = () => {
  return isLocal && !isPreview;
};

// Toggle preview mode
export const togglePreviewMode = () => {
  const currentPreview = isPreviewMode();
  localStorage.setItem("alertique_preview_mode", (!currentPreview).toString());
  // Reload page to apply changes
  window.location.reload();
};

// Force local backend (for development)
export const forceLocalBackend = () => {
  localStorage.setItem("alertique_backend_location", "local");
  localStorage.setItem("alertique_database_type", "sqlite");
  console.log("🔧 Forced local backend - reloading page");
  window.location.reload();
};

// Clear all backend preferences
export const clearBackendPreferences = () => {
  localStorage.removeItem("alertique_backend_location");
  localStorage.removeItem("alertique_database_type");
  localStorage.removeItem("alertique_preview_mode");
  console.log("🔧 Cleared all backend preferences - reloading page");
  window.location.reload();
};

// Get current API configuration
export const getApiConfig = () => {
  const backend =
    BACKEND_CONFIG[currentBackendLocation] || BACKEND_CONFIG.local;
  const database =
    DATABASE_CONFIG[currentDatabaseType] || DATABASE_CONFIG.sqlite;

  const config = {
    ...backend,
    database: database.name,
    databaseType: database.type,
    description: `${backend.description} with ${database.description}`,
  };

  // Only log debug info once per session to avoid spam
  if (!window._apiConfigLogged) {
    console.log("🔧 API Config:", {
      backend: currentBackendLocation,
      database: currentDatabaseType,
      localStorage: {
        backend: localStorage.getItem("alertique_backend_location"),
        database: localStorage.getItem("alertique_database_type"),
      },
      envVar: getEnvVar("VITE_API_BASE_URL", "not set"),
    });
    window._apiConfigLogged = true;
  }

  // Force production URL when deployed to Vercel
  if (isProductionDeployment()) {
    config.baseURL = "https://alertique-backend.onrender.com";
  }
  // In local development with Vite dev server, use relative paths to go through proxy
  // This allows the proxy to rewrite cookies so they work with localhost:5173
  else if (
    isLocal &&
    currentBackendLocation === "local" &&
    typeof window !== "undefined" &&
    (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1")
  ) {
    // Use relative path so requests go through Vite proxy
    // The proxy will forward to http://127.0.0.1:5001 and rewrite cookies
    config.baseURL = "";
    console.log("🔧 Using relative paths for Vite proxy (cookie rewriting enabled)");
  }
  // Mobile detection: If accessing from mobile and using local backend, use computer's IP
  else if (
    currentBackendLocation === "local" &&
    typeof window !== "undefined" &&
    window.location.hostname !== "localhost" &&
    window.location.hostname !== "127.0.0.1"
  ) {
    // Replace localhost/127.0.0.1 with the current hostname (your computer's IP)
    config.baseURL = config.baseURL.replace(
      /http:\/\/(localhost|127\.0\.0\.1):5001/,
      `http://${window.location.hostname}:5001`
    );
    console.log(
      "📱 Mobile detected - Using IP address for backend:",
      config.baseURL
    );
  }
  // Only override with environment variable if no backend location is set in localStorage
  else if (
    getEnvVar("VITE_API_BASE_URL") &&
    !localStorage.getItem("alertique_backend_location")
  ) {
    config.baseURL = getEnvVar("VITE_API_BASE_URL");
  }

  return config;
};

// Get base URL for API calls
export const getApiBaseUrl = () => {
  return getApiConfig().baseURL;
};

// Switch backend location
export const switchBackendLocation = (backendLocation) => {
  if (!isLocal) {
    console.warn("Backend switching is only available in local development");
    return;
  }

  if (BACKEND_CONFIG[backendLocation]) {
    localStorage.setItem("alertique_backend_location", backendLocation);

    // Automatically set appropriate database type for the backend
    const appropriateDatabase =
      backendLocation === "remote" ? "postgresql" : "sqlite";
    localStorage.setItem("alertique_database_type", appropriateDatabase);

    // Reload page to apply new configuration
    window.location.reload();
  } else {
    console.error(`Invalid backend location: ${backendLocation}`);
  }
};

// Switch database type
export const switchDatabaseType = (databaseType) => {
  if (!isLocal) {
    console.warn("Database switching is only available in local development");
    return;
  }

  if (DATABASE_CONFIG[databaseType]) {
    localStorage.setItem("alertique_database_type", databaseType);
    console.log(`🔧 Switched to ${databaseType} database - reloading page`);
    window.location.reload();
  } else {
    console.error(`Invalid database type: ${databaseType}`);
  }
};

// Switch environment (legacy support)
export const switchEnvironment = (environment) => {
  if (!isLocal) {
    console.warn(
      "Environment switching is only available in local development"
    );
    return;
  }

  if (API_CONFIG[environment]) {
    localStorage.setItem("alertique_environment", environment);
    console.log(`🔧 Switched to ${environment} environment - reloading page`);
    window.location.reload();
  } else {
    console.error(`Invalid environment: ${environment}`);
  }
};

// Get available backend locations
export const getAvailableBackendLocations = () => {
  return Object.keys(BACKEND_CONFIG).map((key) => ({
    key,
    name: BACKEND_CONFIG[key].name,
  }));
};

// Get available database types
export const getAvailableDatabaseTypes = () => {
  return Object.keys(DATABASE_CONFIG).map((key) => ({
    key,
    name: DATABASE_CONFIG[key].name,
  }));
};

// Get available environments (legacy support)
export const getAvailableEnvironments = () => {
  return Object.keys(API_CONFIG);
};

// Get environment info
export const getEnvironmentInfo = () => {
  return {
    current: currentEnvironment,
    available: getAvailableEnvironments(),
    config: API_CONFIG[currentEnvironment] || API_CONFIG.development,
  };
};

// Get backend info
export const getBackendInfo = () => {
  return {
    location: currentBackendLocation,
    database: currentDatabaseType,
    config: getApiConfig(),
    isLocal: isLocal,
    isPreview: isPreview,
  };
};

// Debug function to log current configuration
export const debugApiConfig = () => {
  console.log("🔧 Debug API Configuration:", {
    backend: getBackendInfo(),
    environment: getEnvironmentInfo(),
    localStorage: {
      backend: localStorage.getItem("alertique_backend_location"),
      database: localStorage.getItem("alertique_database_type"),
      environment: localStorage.getItem("alertique_environment"),
      preview: localStorage.getItem("alertique_preview_mode"),
    },
    envVars: {
      VITE_API_BASE_URL: getEnvVar("VITE_API_BASE_URL"),
      VITE_BACKEND_LOCATION: getEnvVar("VITE_BACKEND_LOCATION"),
      VITE_DATABASE_TYPE: getEnvVar("VITE_DATABASE_TYPE"),
      VITE_ENVIRONMENT: getEnvVar("VITE_ENVIRONMENT"),
      MODE: getEnvVar("MODE"),
      DEV: getEnvVar("DEV"),
    },
  });
};
